if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;

        /* -------------------------------------------------------------
         * Gift-box debugging utilities (shared with gift-box-add-to-cart.js)
         * ------------------------------------------------------------- */
        if (typeof window.__GIFTPROPS_DEBUG__ === 'undefined') {
          // Default to off in production; can be toggled in DevTools.
          window.__GIFTPROPS_DEBUG__ = true;
        }

        window.logGiftboxDebug = window.logGiftboxDebug || function (msg, payload) {
          if (!window.__GIFTPROPS_DEBUG__) return;
          console.log(`%c[GiftBox Debug] ${msg}`, 'color:#8A2BE2;font-weight:bold', payload);
        };

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        let productIsPersonalised = false;
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        let formData = new FormData(this.form);

        // Debug: log initial add-to-cart payload (before any scrub)
        window.logGiftboxDebug('Initial FormData from ProductForm', Array.from(formData.entries()));

        /*
         * ---------------------------------------------
         * Remove orphaned gift-box line-item for contexts
         * ---------------------------------------------
         * PDP normal add-to-cart  → scrub when _hasGiftBox !== 'true'
         * PLP quick-add (inside <quick-add-modal>) → same scrub, but we use
         *    a stricter selector because quick-add HTML can be cloned and the
         *    disabled attribute may be lost.
         */

        const fromQuickAdd = Boolean(this.closest('quick-add-modal'));
        const giftingEnabled = Boolean(this.closest('product-form') && this.closest('product-form').dataset.gifting === 'true');

        const hasGiftBox = formData.get('properties[_hasGiftBox]') === 'true';

        if (giftingEnabled && !hasGiftBox) {
          const cleaned = new FormData();
          for (let [key, value] of formData.entries()) {
            if (key.startsWith('items[')) continue;
            if (key === 'properties[_giftProductHandle]' || key === 'properties[_giftPid]' || key === 'properties[_groupId]') continue;
            // Drop empty top-level id
            if (key === 'id' && (!value || value === '')) continue;
            cleaned.append(key, value);
          }
          formData = cleaned;

          window.logGiftboxDebug('FormData after gift-box scrub', Array.from(formData.entries()));
        }
        // replace body later

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Ensure a valid variant id is present before submitting. Some edge cases
        // (e.g. cloned quick-add forms or disabled inputs being toggled) can
        // result in FormData missing the `id` field, which causes a 400 from
        // /cart/add. Prefer the value from the hidden variant input.
        if (!formData.get('id') || formData.get('id') === '') {
          // 1) Hidden input[name="id"] inside the form
          let recoveredId = this.form.querySelector('input[name="id"]')?.value;
          // 2) Alternate hidden field with class .product-variant-id
          if (!recoveredId) recoveredId = this.form.querySelector('.product-variant-id')?.value;
          // 3) Submit button data attribute set by Liquid
          if (!recoveredId) recoveredId = this.submitButton?.dataset?.prodVariantId;
          // 4) Variant id in URL (e.g., ?variant=123)
          if (!recoveredId) recoveredId = new URLSearchParams(window.location.search).get('variant');

          if (recoveredId) {
            // Coerce to numeric string to avoid any stray whitespace or types
            const coerced = String(parseInt(recoveredId, 10));
            formData.set('id', coerced);
            window.logGiftboxDebug('Repaired missing variant id in FormData', coerced);
          } else {
            window.logGiftboxDebug('Variant id still missing before /cart/add. FormData keys', Array.from(formData.keys()));
          }
        }

        const currentGroupId = formData.get('properties[_groupId]');
        const groupId = currentGroupId || Date.now().toString();

        if (
          document.querySelector('personalisation-drawer') &&
          Boolean(formData.get(`properties[${window.personalisationStrings.initialsProperty}]`))
        ) {
          productIsPersonalised = true;

          if (!currentGroupId) {
            formData.append('properties[_groupId]', groupId);
          }
        }

        // Log the final payload (safe view) before submit
        try {
          const safeEntries = Array.from(formData.entries()).map(([k, v]) => [k, typeof v === 'string' ? v : '[Blob]']);
          const idsOnly = safeEntries.filter(([k]) => k === 'id' || k.endsWith('[id]'));
          window.logGiftboxDebug('About to POST /cart/add – final FormData', safeEntries);
          window.logGiftboxDebug('FormData ids', idsOnly);
        } catch (e) {}

        config.body = formData;

        window.logGiftboxDebug('POST →', routes.cart_add_url);
        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const clone = response.clone();
            const text = await clone.text();
            window.logGiftboxDebug('Response status from /cart/add', { ok: response.ok, status: response.status, body: text });
            try { return JSON.parse(text); } catch (_) { return { status: !response.ok, description: text || 'Non-JSON response' }; }
          })
          .then(async (initialResponse) => {
            if (productIsPersonalised) {
              this.addMonogramProductToCart(groupId);
            }

            let response = initialResponse;

            if (response.status) {
              // If the platform reports missing id, attempt a JSON fallback
              const message = response.message || response.description || '';
              const idForFallback = formData.get('id');
              if (idForFallback && /missing|invalid\: id/i.test(message || '')) {
                window.logGiftboxDebug('Falling back to JSON /cart/add with id', idForFallback);
                response = await this.addToCartJsonFallback(idForFallback);
              }
            }

            // If still error, handle and stop
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            // Debug: log cart-add response so we can verify gift-box lines in cart state
            window.logGiftboxDebug('Cart add response', response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      addMonogramProductToCart(groupId) {
        const productId = document.querySelector('personalisation-drawer').dataset.monogramProduct;
        const quantity = 1;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(productId),
            quantity: quantity,
            properties: {
              _groupId: groupId,
              _addon: true,
              _addonType: 'Personalisation',
            },
          }),
        })
          .then(async (response) => {
            if (response.status) {
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  productVariantId: productId,
                });
                await window.updateCartItemCount();
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       * Fallback add-to-cart using JSON body. Some browsers/sites can be picky
       * with multipart FormData; this tries the JSON endpoint shape.
       */
      addToCartJsonFallback(variantId) {
        const sections = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : undefined;
        const sectionsUrl = window.location.pathname + window.location.search;

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1,
            ...(sections ? { sections } : {}),
            sections_url: sectionsUrl,
          }),
        }).then(async (res) => {
          const txt = await res.clone().text();
          window.logGiftboxDebug('JSON fallback /cart/add response', { ok: res.ok, status: res.status, body: txt });
          try { return JSON.parse(txt); } catch(_) { return { status: !res.ok, description: txt }; }
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
 /**
       * Fetches Shopify’s AJAX cart object and updates
       * .drawer__heading .item_count to show the current items_count.
       */
 window.updateCartItemCount = async function () {
  fetch('/cart.js', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
    .then((res) => res.json())
    .then((cart) => {
      // cart.items_count holds the total number of items in the cart
      const count = cart.item_count;
      const drawerCountEl = document.querySelector('.drawer__heading .item_count');
      if (drawerCountEl) {
        drawerCountEl.innerHTML = `(${count})`;
      }
      const bubble = document.querySelector('.cart-count-bubble');
      if (bubble) {
        // First span: visible bubble (aria-hidden)
        const visibleSpan = bubble.querySelector('span[aria-hidden="true"]');
        // Second span: visually-hidden screen-reader text
        const hiddenSpan  = bubble.querySelector('.visually-hidden');

        if (visibleSpan) {
          visibleSpan.textContent = count;
        }
        if (hiddenSpan) {
          // Use singular "item" if count === 1, plural "items" otherwise
          const suffix = count === 1 ? 'item' : 'items';
          hiddenSpan.textContent = `${count} ${suffix}`;
        }
      }
    })
    .catch((err) => {
      console.error('Failed to fetch cart.js:', err);
    });
}